import { useState, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  VersichertenSession,
  getVersichertenSession,
  setVersichertenSession,
  clearVersichertenSession,
  isVersichertenSessionValid,
} from "../lib/versichertenSession";

function normalizeProfileId(
  value: string | number | undefined | null
): string | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value).trim();
  }
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toUnixMs(value: unknown): number | null {
  const normalizeEpoch = (parsed: number): number => {
    if (parsed > 0 && parsed < 1_000_000_000_000) {
      return parsed * 1000;
    }
    return parsed;
  };

  if (typeof value === "number" && Number.isFinite(value)) {
    return normalizeEpoch(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return normalizeEpoch(parsed);

    const parsedDate = Date.parse(trimmed);
    return Number.isFinite(parsedDate) ? parsedDate : null;
  }
  return null;
}

export function useVersichertenSession(versichertenProfileId?: string) {
  const [session, setSession] = useState<VersichertenSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use `as any` because the generated API types won't include versichertenSessions yet
  const apiRef = api as any;
  const touchSession = useMutation(
    apiRef.auth.versichertenSessions.updateVersichertenSessionActivity
  );

  useEffect(() => {
    setSession(getVersichertenSession());
    setIsLoading(false);
  }, []);

  const isValid = versichertenProfileId
    ? isVersichertenSessionValid(versichertenProfileId)
    : session !== null;

  const createSession = useCallback(
    (
      versichertenProfileId: string,
      sessionToken: string,
      expiresAt: number
    ) => {
      const normalizedProfileId = normalizeProfileId(versichertenProfileId);
      const normalizedExpiresAt = toUnixMs(expiresAt);

      if (normalizedProfileId === null || normalizedExpiresAt === null) {
        clearSession();
        return;
      }

      const newSession: VersichertenSession = {
        versichertenProfileId: normalizedProfileId,
        sessionToken,
        expiresAt: normalizedExpiresAt,
        validatedAt: Date.now(),
      };
      setVersichertenSession(newSession);
      setSession(newSession);
    },
    []
  );

  const clearSession = useCallback(() => {
    clearVersichertenSession();
    setSession(null);
  }, []);

  // Keep-alive: update activity every 60 seconds
  const keepAlive = useCallback(async () => {
    const current = getVersichertenSession();
    if (!current) return false;
    try {
      const updated = await touchSession({
        sessionToken: current.sessionToken,
      });
      // If server says invalid, clear local session
      if (!updated) {
        clearSession();
      }
      return updated;
    } catch {
      return false;
    }
  }, [clearSession, touchSession]);

  // Auto keep-alive interval
  useEffect(() => {
    if (!isValid || !session) return;

    const interval = setInterval(() => {
      void keepAlive();
    }, 60_000);

    return () => clearInterval(interval);
  }, [isValid, session, keepAlive]);

  return {
    session,
    isValid,
    isLoading,
    createSession,
    clearSession,
    keepAlive,
  };
}
